import { memo, useCallback, useEffect, useRef } from 'react';
import { LoginLayout } from './login-layout';
import { useAnalytics } from '@restworld/utils-common';
import { Button } from '@restworld/ui-ds-base';
import { useTranslation } from 'next-i18next';
import { useAccountsService } from '@restworld/data-services';
import { AxiosError, AxiosResponse } from 'axios';
import {
  arrivalDestination,
  clearConsumedLoginToken,
  resolveAndReportLoginDestination,
  useAuthContext,
} from '@restworld/feature-authentication';
import { LoginByEmailForm, LoginByEmailHeader, MagicLinkExpired, MagicLinkUsedHeader } from './login-by-email-form';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  ContentType,
  activeContent,
  existingPhoneNumber as existingPhoneNumberAtom,
  setActiveContent,
} from '../../context/auth-flow';
import { LoginLinkSentForm, LoginLinkSentHeader } from './login-link-sent-form';
import { useEmailLoginForm } from './hooks/use-email-login-form';
import { LoginByPhoneForm, LoginByPhoneHeader } from './login-by-phone-form';
import { VerifyOtpForm, VerifyOtpHeader } from './verify-otp-form';
import { useRouter } from 'next/router';
import { Loader } from '../common';
import { queryAfterLoginFromUrl, rememberVerifiedToken, shouldVerifyToken } from './magic-link';

export const LoginDesktopComponent = memo(({ token, redirectUrl, email }: LoginDesktopComponentProps) => {
  const acc = useAccountsService();
  const authContext = useAuthContext();
  const activeContentType = useAtomValue(activeContent);
  const setActiveContentType = useSetAtom(setActiveContent);
  const { setLoading: setEmailVerificationLoading } = useEmailLoginForm({
    isModal: false,
    currentUrl: redirectUrl,
  });
  const isEmailLogin = activeContentType === ContentType.EMAIL_LOGIN;
  const isEmailSent = activeContentType === ContentType.EMAIL_SENT;
  const isPhoneLogin = activeContentType === ContentType.PHONE_NUMBER_LOGIN;
  const isVerifyOtp = activeContentType === ContentType.OTP_VERIFICATION;
  const linkHasExpired = activeContentType === ContentType.MAGIC_LINK_EXPIRED;
  const linkUsed = activeContentType === ContentType.MAGIC_LINK_USED;
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation('auth-flow');
  const action = router.query?.action;
  const job_position_id = router.query?.job_position_id;
  const worker_id = router.query?.worker_id;
  const alreadyAuthenticated = authContext?.isAuthenticated;
  const [, setExistingPhoneNumber] = useAtom(existingPhoneNumberAtom);
  const { doesNumberExistInURL, phoneNumberFromURL } = usePhoneNumberFromURL();

  useEffect(() => {
    if (doesNumberExistInURL) {
      setActiveContentType(ContentType.PHONE_NUMBER_LOGIN);
      setExistingPhoneNumber({ phoneNumber: phoneNumberFromURL });
    }
  }, [doesNumberExistInURL, phoneNumberFromURL, setActiveContentType, setExistingPhoneNumber]);
  const removeFromUrl = useCallback(
    ({ removeAllParams = false }: { removeAllParams?: boolean }) => {
      const { pathname, query } = router;
      // The link has been spent, either way it went. Held any longer the value would keep
      // answering every caller for the life of the tab, and AuthGuard would go on treating
      // this as a login in progress instead of redirecting a worker who is now signed in.
      clearConsumedLoginToken();

      // The destination comes from what the page was **opened** with as well as from the
      // router, because on a cold load the router has not parsed the query yet and this
      // `replace` is what would throw the destination away. Traced on dev.worker on 22/09;
      // see `queryAfterLoginFromUrl` and `login-destination.ts`.
      const next = queryAfterLoginFromUrl(query, { removeAllParams });

      router.replace({ pathname, query: next }, undefined, { shallow: true });
    },
    [router]
  );

  const handleVerification = useCallback(
    (token: string) => {
      setEmailVerificationLoading(true);
      acc
        // The destination the link arrived with goes up with the token, so the backend can
        // answer with one for a link minted before it started recording them on the row. It is
        // sanitised there before being echoed.
        .verifyMagicLink(token, arrivalDestination())
        .then((response: AxiosResponse) => {
          setEmailVerificationLoading(false);
          const jwtToken = response?.data?.jwt || null;
          if (authContext?.verifyToken) {
            authContext.initializeAuthentication(jwtToken).then(() => {
              // **Navigate from where the destination is known**, instead of writing it back
              // into the URL for `AuthGuard` to find on a later render. That handover is what
              // lost it on every cold load for twenty days, and deleting it is worth more than
              // protecting it: here the value is in hand, in the same function, with no router
              // readiness to race.
              //
              // `AuthGuard` keeps its own copy of the decision for every *other* way of
              // arriving at `/login` already signed in — a pasted link, a bookmark, a second
              // tab. The two agree because they call the same resolver.
              const { path } = resolveAndReportLoginDestination(response?.data?.redirect_url, 'login_screen');

              if (path) {
                // **One navigation, not two.** Tidying the URL of a page we are leaving buys
                // nothing, and `router.replace` immediately followed by `router.push` does not
                // queue: the second cancels the first, and Next answers the cancellation with a
                // `routeChangeError` carrying `cancelled: true`
                // (`shared/lib/router/router.js:864`). Everything bound to the router's events
                // sees that — `nextjs-progressbar` and the two analytics hooks among them — so
                // the old pair put a spurious cancellation into every single token login, which
                // is indistinguishable from a real one at each of those listeners.
                //
                // `replace` and not `push` for the same reason the token bootstrap uses it: the
                // history entry we are on is the spent login link, and it should be overwritten
                // rather than left behind a Back button that lands on `/login` and bounces
                // forward again.
                clearConsumedLoginToken();
                router.replace(path);
              } else {
                // No destination, so we are staying on `/login`: tidy the spent credential out
                // of the address bar. This is the only remaining caller that needs to.
                removeFromUrl({ removeAllParams: false });
              }
            });
            // if (alreadyAuthenticated) {
            //   window.location.reload();
            // }
          }
          // trackEvent('Auth flow', {
          //   step: 'Email login link validated successfully',
          //   mode: 'page',
          // });
          trackEvent('complete_signin', {
            method: 'email',
            email: authContext?.user?.email,
            user_id: authContext?.user?.id,
          });
        })
        .catch((error: AxiosError) => {
          setEmailVerificationLoading(false);
          if (!alreadyAuthenticated) {
            const statusCode = parseInt(error.code);
            if (statusCode === 401 || statusCode === 404 || statusCode === 400) {
              setActiveContentType(ContentType.MAGIC_LINK_EXPIRED);
              // trackEvent('Auth flow', {
              //   step: 'Email login link expired',
              //   mode: 'page',
              //   type: 'error',
              // });
            } else if (statusCode === 409) {
              // **A spent link is not an error screen any more** (decision 149).
              //
              // `assessment_chat` tokens are single use: `LoginToken.consume/1` in
              // core-services burns the row on the first successful verification, whatever
              // the purpose. The interview link lives in a WhatsApp message and the window is
              // three days, so a worker who comes back to the same message the next day —
              // which is what we ask them to do — was being shown "this link has already been
              // used" and stopping there.
              //
              // The ordinary login form instead, with the destination still in the URL. A
              // worker whose session is alive never sees it: `AuthGuard` takes them to
              // `redirect_url` on the next render. One whose session is gone signs in and
              // arrives at the same place, which is the most a spent credential can honestly
              // buy them. `MAGIC_LINK_USED` and its header stay for any caller that still
              // wants to say it; nothing routes here any more.
              setActiveContentType(ContentType.EMAIL_LOGIN);
            } else {
              setActiveContentType(ContentType.EMAIL_LOGIN);
            }
          }
          removeFromUrl({ removeAllParams: alreadyAuthenticated });
        });
    },
    [
      acc,
      alreadyAuthenticated,
      authContext,
      removeFromUrl,
      router,
      setActiveContentType,
      setEmailVerificationLoading,
      trackEvent,
    ]
  );

  /**
   * One POST per token, whatever React does with this effect.
   *
   * Measured on stage on 21/09: **four** `POST /accounts/login` with the same token inside
   * seven milliseconds, three answered 409 "token already used" and one 200. The 409 landed
   * first, its handler put the screen into `MAGIC_LINK_USED` and called `removeFromUrl`, and
   * the worker ended up on `/profilo` while the login that actually succeeded was still in
   * flight.
   *
   * The effect re-runs because its dependencies change identity during the login it is
   * performing: `handleVerification` is rebuilt whenever `authContext` changes, and
   * `removeFromUrl` whenever `router` does. Both change the moment the first response lands,
   * so the effect fires again while `token` is still truthy. StrictMode's double invoke in
   * development doubles it again.
   *
   * A ref and not a dependency change: the token is the identity of the attempt, so the
   * guard remembers the value rather than a boolean, and a genuinely different link opened
   * in the same tab is still honoured.
   */
  const verifiedToken = useRef<string | null>(null);

  useEffect(() => {
    if (token) {
      if (action && job_position_id && worker_id && alreadyAuthenticated) {
        removeFromUrl({ removeAllParams: false });
      } else if (shouldVerifyToken(token, verifiedToken.current)) {
        verifiedToken.current = token;
        // Remembered outside the component too: a ref dies with the mount, and a remount
        // while the POST is still in flight would send the same token again (Codex peer
        // review of #413). `clearConsumedLoginToken` has not run yet at that point, so the
        // second mount reads the same token from module state and would happily repeat it.
        if (!rememberVerifiedToken(token)) return;
        handleVerification(token);
      }
    }
  }, [action, alreadyAuthenticated, handleVerification, job_position_id, removeFromUrl, token, worker_id]);

  // Every step after the email one can go back to it; the phone code step goes back to the phone number.
  const showBackButton = isEmailSent || isPhoneLogin || isVerifyOtp;

  const onBack = () => {
    if (isVerifyOtp) {
      setActiveContentType(ContentType.PHONE_NUMBER_LOGIN);
    } else {
      setActiveContentType(ContentType.EMAIL_LOGIN);
    }
  };

  useEffect(() => {
    if (!isEmailLogin && !isEmailSent && !isPhoneLogin && !isVerifyOtp && !linkHasExpired && !linkUsed) {
      setActiveContentType(ContentType.EMAIL_LOGIN);
      // trackEvent('Auth flow', { phase: 'Email login', step: 'Start email login', mode: 'page' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // if (!!token && token !== '') return <Loader />;

  return (
    <LoginLayout
      hideImageOnMobile={!isEmailLogin}
      footer={
        showBackButton ? (
          <Button variant="ghost" onClick={onBack} data-cy="login-back-button">
            {t('login_back_to_login')}
          </Button>
        ) : null
      }
    >
      <div className="flex flex-col gap-8 w-full">
        {isEmailLogin && <LoginByEmailHeader isModal={false} />}
        {isEmailSent && <LoginLinkSentHeader isModal={false} />}
        {isPhoneLogin && <LoginByPhoneHeader isModal={false} />}
        {isVerifyOtp && <VerifyOtpHeader isModal={false} />}
        {linkHasExpired && <MagicLinkExpired isModal={false} />}
        {linkUsed && <MagicLinkUsedHeader isModal={false} />}
        {(isEmailLogin || linkHasExpired || linkUsed) && (
          <LoginByEmailForm
            {...{
              isModal: false,
              activeUrl: redirectUrl,
              email,
            }}
          />
        )}
        {isEmailSent && <LoginLinkSentForm isModal={false} />}
        {isPhoneLogin && <LoginByPhoneForm isModal={false} />}
        {isVerifyOtp && <VerifyOtpForm isModal={false} />}
      </div>
    </LoginLayout>
  );
});

export interface LoginDesktopComponentProps {
  children?: React.ReactNode;
  redirectToPath: (path: string, query?: { [key: string]: string | boolean }) => void;
  token?: string;
  redirectUrl?: string;
  email?: string;
}
export const usePhoneNumberFromURL = () => {
  const router = useRouter();
  const phoneNumberFromURL = router.query?.phone_number as string;
  const doesNumberExistInURL = !!phoneNumberFromURL && phoneNumberFromURL.length > 5;
  return { doesNumberExistInURL, phoneNumberFromURL };
};
